// src/components/RPA/TaskBoard.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import SidePanel from './ui/SidePanel';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// Normalize Socket target (strip '/api') – same logic as RpaDashboard
const SOCKET_URL = (() => {
  try {
    const u = new URL(API_URL);
    return `${u.protocol}//${u.host}`;
  } catch {
    return API_URL.replace(/\/api\/?$/, '');
  }
})();

const STATUS_LABELS = {
  queued: 'Queued',
  in_progress: 'In Progress',
  done: 'Done'
};

const STATUS_ORDER = ['queued', 'in_progress', 'done'];

const TaskBoard = ({ isOpen, onClose, project }) => {
  const [tasks, setTasks] = useState([]);
  const [admins, setAdmins] = useState([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('queued');
  const [assigneeId, setAssigneeId] = useState('');

  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const socketRef = useRef(null);
  const socketHandlerRef = useRef(null);

  const projectId = project?._id || project?.id || null;

  const clearForm = () => {
    setTitle('');
    setDescription('');
    setStatus('queued');
    setAssigneeId('');
  };

  const getSocket = useCallback(() => {
    if (typeof window !== 'undefined' && window.__RPA_SOCKET__) {
      return window.__RPA_SOCKET__;
    }
    const s = io(SOCKET_URL, {
      withCredentials: true,
      autoConnect: true,
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000
    });
    if (typeof window !== 'undefined') window.__RPA_SOCKET__ = s;
    return s;
  }, []);

  /** --------------------- Data fetchers --------------------- */

  const fetchAdmins = useCallback(async () => {
    try {
      setLoadingAdmins(true);
      const res = await axios.get(`${API_URL}/admin/rpa/admins`, {
        withCredentials: true
      });
      setAdmins(res.data?.admins || []);
    } catch (err) {
      console.error('[TaskBoard] fetchAdmins failed:', err);
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingTasks(true);
      setError('');
      const res = await axios.get(`${API_URL}/admin/rpa/tasks/by-project/${projectId}`, {
        withCredentials: true
      });
      const serverTasks = res.data?.tasks || [];
      setTasks(serverTasks);
    } catch (err) {
      console.error('[TaskBoard] fetchTasks failed:', err);
      setError('Failed to load tasks for this project.');
    } finally {
      setLoadingTasks(false);
    }
  }, [projectId]);

  /** --------------------- Socket wiring --------------------- */

  useEffect(() => {
    if (!isOpen || !projectId) return;

    const socket = getSocket();
    socketRef.current = socket;

    // We piggyback on the existing RPA channel: rpa_workflow_update,
    // but listen specifically for task_* events with matching projectId.
    const handler = (evt) => {
      if (!evt || !evt.type || !String(evt.type).startsWith('task_')) return;
      if (!evt.projectId || String(evt.projectId) !== String(projectId)) return;
      if (!evt.task) return;

      const task = evt.task;

      setTasks((prev) => {
        // Handle deletion if you later broadcast task_deleted; for now we only upsert.
        if (evt.type === 'task_deleted') {
          return prev.filter((t) => String(t._id) !== String(task._id));
        }

        const exists = prev.some((t) => String(t._id) === String(task._id));
        if (exists) {
          return prev.map((t) => (String(t._id) === String(task._id) ? task : t));
        }
        return [...prev, task];
      });

      console.log('[TaskBoard] Received socket task event:', evt.type, 'taskId:', task._id);
    };

    socket.on('rpa_workflow_update', handler);
    socketHandlerRef.current = handler;

    // Optionally identify as dashboard
    socket.emit('rpa_dashboard_ready');

    return () => {
      if (socketRef.current && socketHandlerRef.current) {
        socketRef.current.off('rpa_workflow_update', socketHandlerRef.current);
      }
    };
  }, [isOpen, projectId, getSocket]);

  /** --------------------- Lifecycle --------------------- */

  useEffect(() => {
    if (!isOpen || !projectId) return;
    fetchTasks();
    fetchAdmins();
  }, [isOpen, projectId, fetchTasks, fetchAdmins]);

  useEffect(() => {
    if (!isOpen) {
      setTasks([]);
      clearForm();
      setError('');
      setSuccessMessage('');
    }
  }, [isOpen]);

  /** --------------------- Actions --------------------- */

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!projectId) return;

    if (!title.trim()) {
      setError('Task title is required.');
      return;
    }

    try {
      setError('');
      setSuccessMessage('');

      const payload = {
        projectId,
        title: title.trim(),
        status,
        assigneeId: assigneeId || null,
        metadata: {
          description: description || ''
        }
      };

      const res = await axios.post(`${API_URL}/admin/rpa/tasks`, payload, {
        withCredentials: true
      });

      const created = res.data?.task;
      if (created) {
        setTasks((prev) => [...prev, created]);
      }

      setSuccessMessage('Task created.');
      clearForm();
    } catch (err) {
      console.error('[TaskBoard] handleCreateTask failed:', err);
      setError(err.response?.data?.message || 'Failed to create task.');
    }
  };

  const handleUpdateTask = async (taskId, fields) => {
    if (!taskId) return;

    try {
      setError('');
      setSuccessMessage('');

      const payload = {
        taskId,
        ...fields
      };

      const res = await axios.post(`${API_URL}/admin/rpa/tasks`, payload, {
        withCredentials: true
      });

      const updated = res.data?.task;
      if (updated) {
        setTasks((prev) =>
          prev.map((t) => (String(t._id) === String(updated._id) ? updated : t))
        );
      }

      setSuccessMessage('Task updated.');
    } catch (err) {
      console.error('[TaskBoard] handleUpdateTask failed:', err);
      setError(err.response?.data?.message || 'Failed to update task.');
    }
  };

  const handleStatusChange = (taskId, newStatus) => {
    return handleUpdateTask(taskId, { status: newStatus });
  };

  const handleReassign = (taskId, newAssigneeId) => {
    return handleUpdateTask(taskId, { assigneeId: newAssigneeId || null });
  };

  const handleAccept = (taskId) => {
    return handleUpdateTask(taskId, { acceptanceAction: 'accept' });
  };

  const handleDecline = (taskId) => {
    return handleUpdateTask(taskId, { acceptanceAction: 'decline' });
  };

  const handleDelete = async (taskId) => {
    if (!taskId) return;
    if (!window.confirm('Delete this task?')) return;

    try {
      await axios.delete(`${API_URL}/admin/rpa/tasks/${taskId}`, {
        withCredentials: true
      });

      setTasks((prev) => prev.filter((t) => String(t._id) !== String(taskId)));
      setSuccessMessage('Task deleted.');
    } catch (err) {
      console.error('[TaskBoard] handleDelete failed:', err);
      setError(err.response?.data?.message || 'Failed to delete task.');
    }
  };

  /** --------------------- Helpers --------------------- */

  const tasksByStatus = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = tasks.filter((t) => t.status === s);
    return acc;
  }, { queued: [], in_progress: [], done: [] });

  const getAssigneeLabel = (task) => {
    if (!task.assigneeId && !task.assigneeAdmin) return 'Unassigned';
    const adminIdForMatch = task.assigneeId || (task.assigneeAdmin && String(task.assigneeAdmin));
    const match = admins.find((a) => String(a.id) === String(adminIdForMatch));
    if (match) return match.name || match.email || 'Unnamed Admin';
    return 'Assigned (not in list)';
  };

  const renderTaskCard = (task) => {
    const assigneeLabel = getAssigneeLabel(task);
    const acceptance = task.acceptanceStatus || null;

    const showAcceptDecline = !!task.assigneeId && acceptance === 'pending';

    return (
      <div
        key={task._id}
        className="border border-gray-200 rounded-md p-2 mb-2 bg-white shadow-sm flex flex-col gap-1"
      >
        <div className="flex justify-between items-start gap-2">
          <div>
            <div className="font-semibold text-xs text-gray-900">{task.title}</div>
            {task.metadata?.description && (
              <div className="text-[11px] text-gray-600 mt-0.5">
                {task.metadata.description}
              </div>
            )}
          </div>
          <button
            type="button"
            className="text-[10px] text-red-500 hover:text-red-700"
            onClick={() => handleDelete(task._id)}
          >
            Delete
          </button>
        </div>

        <div className="text-[11px] text-gray-600 flex flex-wrap gap-2 mt-1">
          <span className="inline-flex items-center px-2 py-[1px] rounded-full bg-gray-100 text-gray-700">
            Assignee: {assigneeLabel}
          </span>

          {acceptance && (
            <span className="inline-flex items-center px-2 py-[1px] rounded-full text-gray-700 bg-gray-100">
              Request: {acceptance}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 mt-2">
          {/* Status transitions */}
          {task.status !== 'queued' && (
            <button
              type="button"
              onClick={() => handleStatusChange(task._id, 'queued')}
              className="text-[10px] px-2 py-[3px] rounded border border-gray-300 bg-white hover:bg-gray-50"
            >
              Move to Queued
            </button>
          )}
          {task.status !== 'in_progress' && (
            <button
              type="button"
              onClick={() => handleStatusChange(task._id, 'in_progress')}
              className="text-[10px] px-2 py-[3px] rounded border border-blue-300 bg-blue-50 hover:bg-blue-100"
            >
              Move to In Progress
            </button>
          )}
          {task.status !== 'done' && (
            <button
              type="button"
              onClick={() => handleStatusChange(task._id, 'done')}
              className="text-[10px] px-2 py-[3px] rounded border border-green-300 bg-green-50 hover:bg-green-100"
            >
              Mark Done
            </button>
          )}
        </div>

        {showAcceptDecline && (
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="flex-1 text-[10px] px-2 py-[3px] rounded bg-green-600 text-white hover:bg-green-700"
              onClick={() => handleAccept(task._id)}
            >
              Accept
            </button>
            <button
              type="button"
              className="flex-1 text-[10px] px-2 py-[3px] rounded bg-red-600 text-white hover:bg-red-700"
              onClick={() => handleDecline(task._id)}
            >
              Decline
            </button>
          </div>
        )}

        {/* Assignee quick change */}
        <div className="mt-2">
          <label className="block text-[10px] text-gray-500 mb-1">Reassign</label>
          <select
            className="w-full border border-gray-200 rounded px-2 py-[3px] text-[11px]"
            value={task.assigneeId || ''}
            onChange={(e) => handleReassign(task._id, e.target.value || null)}
          >
            <option value="">Unassigned</option>
            {admins.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || a.email}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  /** --------------------- Render --------------------- */

  if (!isOpen || !projectId) return null;

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={`Tasks · ${project?.name || 'Project'}`}>
      <div className="flex flex-col gap-4 h-full">
        {/* Alerts */}
        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
            {successMessage}
          </div>
        )}

        {/* Create Task Form */}
        <form onSubmit={handleCreateTask} className="border border-gray-200 rounded-md p-3 bg-gray-50">
          <div className="flex flex-col gap-2">
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Title<span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summarize the task..."
              />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                className="w-full border border-gray-300 rounded px-2 py-1 text-xs resize-none"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Additional context or instructions"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Initial Status
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[160px]">
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Assign To
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  disabled={loadingAdmins}
                >
                  <option value="">Unassigned</option>
                  {admins.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name || a.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                className="text-xs px-3 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={clearForm}
              >
                Clear
              </button>
              <button
                type="submit"
                className="text-xs px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                disabled={loadingTasks}
              >
                Create Task
              </button>
            </div>
          </div>
        </form>

        {/* Columns */}
        <div className="flex-1 overflow-auto mt-1">
          {loadingTasks ? (
            <div className="text-xs text-gray-500">Loading tasks…</div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {STATUS_ORDER.map((s) => (
                <div key={s} className="border border-gray-200 rounded-md p-2 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-semibold text-gray-700">
                      {STATUS_LABELS[s]}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {tasksByStatus[s].length} task{tasksByStatus[s].length === 1 ? '' : 's'}
                    </div>
                  </div>

                  <div>{tasksByStatus[s].map((task) => renderTaskCard(task))}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SidePanel>
  );
};

export default TaskBoard;





// // src/components/RPA/TaskBoard.js
// import React, { useEffect, useMemo, useState } from 'react';
// import axios from 'axios';
// import SidePanel from './ui/SidePanel';

// const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// const STATUSES = ['queued', 'in_progress', 'done'];

// export default function TaskBoard({ isOpen, onClose, project }) {
//   const projectId = project?._id || project?.id || project;
//   const [loading, setLoading] = useState(false);
//   const [tasks, setTasks] = useState([]);
//   const [error, setError] = useState('');
//   const [saving, setSaving] = useState(false);

//   // create new task
//   const [title, setTitle] = useState('');
//   const [assigneeId, setAssigneeId] = useState('');
//   const [status, setStatus] = useState('queued');

//   const titleText = useMemo(() => {
//     const p = project?.name ? `Tasks · ${project.name}` : 'Project Tasks';
//     return p;
//   }, [project]);

//   const fetchTasks = async () => {
//     if (!projectId) return;
//     try {
//       setLoading(true);
//       setError('');
//       const res = await axios.get(`${API_URL}/admin/rpa/tasks/by-project/${projectId}`, { withCredentials: true });
//       setTasks(res.data?.tasks || []);
//     } catch (e) {
//       console.error('[TaskBoard] list failed:', e);
//       setError('Failed to load tasks');
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => { if (isOpen) fetchTasks(); }, [isOpen]); // eslint-disable-line

//   const onCreate = async (e) => {
//     e.preventDefault();
//     if (!projectId || !title.trim()) return;
//     try {
//       setSaving(true);
//       const payload = {
//         projectId,
//         title: title.trim(),
//         assigneeId: assigneeId || undefined,
//         status
//       };
//       const res = await axios.post(`${API_URL}/admin/rpa/tasks`, payload, { withCredentials: true });
//       setTasks((prev) => [res.data?.task || payload, ...prev]);
//       setTitle(''); setAssigneeId(''); setStatus('queued');
//     } catch (e) {
//       console.error('[TaskBoard] create failed:', e);
//       alert(e?.response?.data?.message || 'Failed to create task.');
//     } finally {
//       setSaving(false);
//     }
//   };

//   const onUpdate = async (taskId, fields) => {
//     try {
//       const payload = { projectId, taskId, ...fields };
//       await axios.post(`${API_URL}/admin/rpa/tasks`, payload, { withCredentials: true });
//       setTasks((prev) => prev.map((t) => (t._id === taskId ? { ...t, ...fields } : t)));
//     } catch (e) {
//       console.error('[TaskBoard] update failed:', e);
//       alert('Failed to update task.');
//     }
//   };

//   const onDelete = async (taskId) => {
//     if (!window.confirm('Delete this task?')) return;
//     try {
//       await axios.delete(`${API_URL}/admin/rpa/tasks/${taskId}`, { withCredentials: true });
//       setTasks((prev) => prev.filter((t) => t._id !== taskId));
//     } catch (e) {
//       console.error('[TaskBoard] delete failed:', e);
//       alert('Failed to delete task.');
//     }
//   };

//   const grouped = useMemo(() => {
//     const g = { queued: [], in_progress: [], done: [] };
//     tasks.forEach((t) => {
//       const s = STATUSES.includes(t.status) ? t.status : 'queued';
//       g[s].push(t);
//     });
//     return g;
//   }, [tasks]);

//   return (
//     <SidePanel isOpen={isOpen} title={titleText} onClose={onClose}>
//       {!projectId ? (
//         <div style={{ color: '#991b1b' }}>Missing project id.</div>
//       ) : (
//         <>
//           <form onSubmit={onCreate} style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
//             <div style={{ fontWeight: 600 }}>New Task</div>
//             <input
//               type="text"
//               value={title}
//               placeholder="Title"
//               onChange={(e) => setTitle(e.target.value)}
//             />
//             <input
//               type="text"
//               value={assigneeId}
//               placeholder="Assignee Admin ID (optional)"
//               onChange={(e) => setAssigneeId(e.target.value)}
//             />
//             <select value={status} onChange={(e) => setStatus(e.target.value)}>
//               {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
//             </select>
//             <button disabled={saving} type="submit" style={{ padding: '6px 10px' }}>
//               {saving ? 'Creating…' : 'Create Task'}
//             </button>
//           </form>

//           {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 8, borderRadius: 6, marginBottom: 8 }}>{error}</div>}

//           {loading ? (
//             <div>Loading…</div>
//           ) : (
//             <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
//               {STATUSES.map((col) => (
//                 <div key={col} style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
//                   <div style={{ padding: 8, borderBottom: '1px solid #e5e7eb', fontWeight: 700, textTransform: 'capitalize' }}>
//                     {col.replace('_', ' ')}
//                   </div>
//                   <div style={{ padding: 8, display: 'grid', gap: 8 }}>
//                     {grouped[col].length === 0 ? (
//                       <div style={{ color: '#9ca3af', fontSize: 12 }}>No tasks</div>
//                     ) : grouped[col].map((t) => (
//                       <div key={t._id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, background: '#f9fafb' }}>
//                         <div style={{ fontWeight: 600 }}>{t.title || 'Untitled'}</div>
//                         <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
//                           Assignee: {t.assigneeId || '—'}
//                         </div>
//                         <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
//                           <select
//                             value={t.status}
//                             onChange={(e) => onUpdate(t._id, { status: e.target.value })}
//                           >
//                             {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
//                           </select>
//                           <input
//                             type="text"
//                             placeholder="Set assignee id"
//                             defaultValue={t.assigneeId || ''}
//                             onBlur={(e) => {
//                               const v = e.target.value.trim();
//                               if (v !== (t.assigneeId || '')) onUpdate(t._id, { assigneeId: v || null });
//                             }}
//                             style={{ flex: 1, minWidth: 120 }}
//                           />
//                           <button
//                             onClick={() => onUpdate(t._id, { assigneeId: null })}
//                             style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 4, padding: '4px 8px' }}
//                             type="button"
//                           >
//                             Unassign
//                           </button>
//                           <button
//                             onClick={() => onDelete(t._id)}
//                             style={{ background: '#ffe3e3', border: '1px solid #ffb3b3', color: '#991b1b', borderRadius: 4, padding: '4px 8px' }}
//                             type="button"
//                           >
//                             Delete
//                           </button>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           )}
//         </>
//       )}
//     </SidePanel>
//   );
// }
